import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { imageReveal } from '../hooks/useAnimations'
import './OverviewMap.css'

const DEFAULT_OVERLAY = {
  top: '15.909%',
  left: '0.451%',
  width: '98.503%',
  height: '81.079%',
  offsetX: 0,
  offsetY: 0,
  refWidth: 3600,
  refHeight: 2700,
}

// On touch devices the map is small enough that users pinch-zoom to tap a state. Modern iOS Safari
// ignores viewport-meta tricks to reset that page zoom, but a real page load always starts at the
// default scale — so on touch-primary devices we bypass SPA routing and let the browser do a full
// navigation. Desktop keeps the SPA transition.
const isTouchPrimary = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches

function bindRegionHandlers(navigate, setHovered, slug) {
  const go = () => {
    const url = `/states/${slug}`
    if (isTouchPrimary()) {
      window.location.assign(url)
    } else {
      navigate(url)
    }
  }
  return {
    role: 'link',
    tabIndex: 0,
    onClick: go,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        go()
      }
    },
    onMouseEnter: () => setHovered(slug),
    onMouseLeave: () => setHovered(null),
    onFocus: () => setHovered(slug),
    onBlur: () => setHovered(null),
  }
}

export default function OverviewMap() {
  const navigate = useNavigate()
  const [mapData, setMapData] = useState(null)
  const [hovered, setHovered] = useState(null)

  useEffect(() => {
    fetch('/map-paths.json')
      .then((r) => r.json())
      .then(setMapData)
      .catch(console.error)
  }, [])

  const overlay = mapData?.overlay ?? DEFAULT_OVERLAY

  const regionClass = (slug) =>
    `overview-map-region${hovered === slug ? ' is-hovered' : ''}`

  const shieldClass = (slug) =>
    `overview-map-shield${hovered === slug ? ' is-hovered' : ''}`

  const labelBySlug = (slug) =>
    mapData?.paths.find((p) => p.slug === slug)?.label ?? slug

  return (
    <motion.div
      className="overview-map"
      variants={imageReveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
    >
      <div className="overview-map-frame">
        <div className="overview-map-zoom-layer">
          <img
            src="/images/overview.png"
            alt="US Highway Shield Designs For Our Modern Times — all fifty-two state route markers on a map of the United States"
            className="overview-map-img"
            draggable={false}
          />
          {mapData && (
            <svg
              className="overview-map-svg overview-map-svg--states"
              viewBox={mapData.viewBox}
              preserveAspectRatio="none"
              style={{
                top: overlay.top,
                left: overlay.left,
                width: overlay.width,
                height: overlay.height,
              }}
              aria-label="Click a state on the map to view its redesigned shield"
            >
              {mapData.paths.map(({ slug, label, d }) => (
                <path
                  key={slug}
                  d={d}
                  aria-label={label}
                  className={regionClass(slug)}
                  {...bindRegionHandlers(navigate, setHovered, slug)}
                />
              ))}
            </svg>
          )}
          {/* Shield hotspots — drawn last so they take pointer priority over the state outlines,
              without disabling them (clicking elsewhere in a state still works). */}
          {mapData?.shields?.length > 0 && (
            <svg
              className="overview-map-svg overview-map-svg--shields"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              aria-label="Click a highway shield to view its state"
            >
              {mapData.shields.map(({ slug, x, y, w, h }) => (
                <rect
                  key={slug}
                  x={x}
                  y={y}
                  width={w}
                  height={h}
                  rx={0.004}
                  aria-label={labelBySlug(slug)}
                  className={shieldClass(slug)}
                  {...bindRegionHandlers(navigate, setHovered, slug)}
                />
              ))}
            </svg>
          )}
        </div>
      </div>
      <p className="overview-map-hint">
        Click any state on the map to view its redesigned shield.
      </p>
    </motion.div>
  )
}
